import React, {
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { CorporateTheme } from "../../utils/themes";
import { Col } from "react-bootstrap";
import StyledText from "../../components/StyledComponents/StyledText";
import {
  HeaderContainer,
  TitleGroup,
  TitleWrapper,
} from "../../components/listing/AMSListing";
import {
  useAddNewQueryMutation,
  useGetColumnsByScopeAllPagesQuery,
  useGetColumnsByScopeQuery,
  useGetQueryByIdMutation,
  useRunQueryMutation,
  useSaveQueryMutation,
} from "../../api/searchQueryApiSlice";
import { Button } from "../../components/form/Button";
import EditIcon from "../../assets/icons/EditIcon";
import CustomSelect from "../../components/form/CustomSelect";
import { StyledInput } from "../../components/listing/ExpandableSearch";
import SearchIcon from "../../assets/icons/SearchIcon";
import styled from "styled-components";
import StyledCheckBox from "../../components/StyledComponents/CheckBox/StyledCheckBox";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import DocumentIcon from "../../assets/icons/document-text";
import FlashIcon from "../../assets/icons/FlashIcon";
import DataTable from "../../components/listing/DataTable";
import { useToast } from "../../components/toast/ToastContext";
import { formatDateDefault, formatDateForApi, formatLabel, isValidDateInput } from "../../utils/helper";
import CriteriaModal from "./CriteriaModal";
import QueryScopeModal from "./QueryScopeModal";
import LoadingCircleIcon from "../../assets/icons/LoadingCircleIcon";
import { useNavigate, useOutletContext, useParams, UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { TableConfig } from "../../types/types";
import QueryResultsListing from "./QueryResultsListing";
import ChevronDownIcon from "../../assets/icons/ChevronDownIcon";
import type { DropResult } from '@hello-pangea/dnd';
import { SkeletonCell } from "../../components/SkeletonCell";
import { useTheme } from "../../middleware/ThemeContext";
import SaveIcon from "../../assets/icons/SaveIcon";
import SimpleBar from "simplebar-react";
import { StyledHorizontalLine } from "../../components/StyledComponents/Styledhr";
import AMSSelect from "../../components/AMSSelect";
import StyledRadio from "../../components/StyledComponents/StyledRadio";
import NoData from "../../components/listing/NoData";
import ConfirmationPrompt from "../../components/StyledComponents/ConfirmationPrompt";

type CriteriaLogicValue = "All" | "Any";

interface CriteriaConditionState {
  id?: number | string;
  column: string;
  condition: string;
  value: any;
}

interface CriteriaGroupState {
  id?: number | string;
  isGroup: true;
  logic: CriteriaLogicValue;
  conditions: CriteriaConditionState[];
}

type CriteriaStateItem = CriteriaConditionState | CriteriaGroupState;

type SelectedCriteriaState = {
  logic?: { label: CriteriaLogicValue; value: CriteriaLogicValue } | string;
  criteria?: CriteriaStateItem[];
};

const isCriteriaGroup = (item: CriteriaStateItem): item is CriteriaGroupState =>
  Boolean(item && (item as CriteriaGroupState).isGroup);

const normalizeLogicChoice = (value?: any): CriteriaLogicValue => {
  if (!value) return "All";
  if (typeof value === "object") {
    const raw = value?.value ?? value?.label;
    if (raw === "Any" || raw === "OR") return "Any";
    if (raw === "All" || raw === "AND") return "All";
  }
  return value === "Any" || value === "OR" ? "Any" : "All";
};

const createLogicOption = (value: CriteriaLogicValue) => ({ label: value, value });

const logicToOperator = (logic?: CriteriaLogicValue | string) =>
  logic === "Any" || logic === "OR" ? "OR" : "AND";

const generateCriteriaId = () => new Date().getTime() + Math.random();

const buildConditionPayload = (
  condition: CriteriaConditionState,
  formatDates: boolean
) => {
  if (!condition?.column || !condition?.condition) return null;
  let value = condition.value;
  if (formatDates && isValidDateInput(value)) {
    value = formatDateForApi(value);
  }
  return {
    field: condition.column,
    operator: condition.condition,
    value,
  };
};

const convertCriteriaItemsToPayload = (
  items: CriteriaStateItem[] | undefined,
  formatDates: boolean
) => {
  if (!Array.isArray(items)) return [];
  const payload: any[] = [];

  items.forEach((item) => {
    if (isCriteriaGroup(item)) {
      const children = item.conditions
        .map((child) => buildConditionPayload(child, formatDates))
        .filter(Boolean);
      if (children.length) {
        payload.push({
          logic: logicToOperator(item.logic),
          conditions: children,
        });
      }
      return;
    }

    const converted = buildConditionPayload(item, formatDates);
    if (converted) {
      payload.push(converted);
    }
  });

  return payload;
};

const transformApiCriteriaItem = (item: any): CriteriaStateItem | null => {
  if (item?.logic && Array.isArray(item?.conditions) && !item?.field && !item?.column) {
    const children: CriteriaConditionState[] = [];

    (item.conditions as any[]).forEach((child: any) => {
      const transformed = transformApiCriteriaItem(child);
      if (!transformed) return;
      if (isCriteriaGroup(transformed)) {
        children.push(...transformed.conditions);
      } else {
        children.push(transformed);
      }
    });

    return {
      id: item?.id ?? generateCriteriaId(),
      isGroup: true,
      logic: normalizeLogicChoice(item?.logic),
      conditions: children,
    };
  }

  if (!item?.field && !item?.column) return null;

  return {
    id: item?.id ?? generateCriteriaId(),
    column: item.field ?? item.column,
    condition: item.operator ?? item.condition,
    value: item.value,
  } as CriteriaConditionState;
};

const transformApiCriteriaConditions = (conditions?: any[]): CriteriaStateItem[] =>
  (conditions ?? [])
    .map((condition) => transformApiCriteriaItem(condition))
    .filter((item): item is CriteriaStateItem => Boolean(item));

const QueryCreation = () => {
  const { theme } = useTheme();
  const userID = JSON.parse(
    localStorage.getItem("authData") || "{}"
  )?.userId;
  const location = useLocation();
  const { item } = location.state || {};
  const { setSelectedColumns, selectedColumns, tableData, setTableData } = useOutletContext<{ selectedColumns: any[], setSelectedColumns: React.Dispatch<React.SetStateAction<any[]>>, tableData: any[], setTableData: React.Dispatch<React.SetStateAction<any[]>> }>();
  const shouldRun = location.state?.runImmediately;
  const showToast: (title: string, subtitle: string, type: "error" | "success", duration?: number) => void = useToast();
  const { queryId } = useParams();

  const initialSelectedColumnsRef = useRef<string[]>([]);
  const initialCriteriaRef = useRef<any>(null);
  const initialSortRef = useRef<any[]>([]);
  const hasUnsavedChangesRef = useRef(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const navigationConfirmedRef = useRef(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState("");
  const [columnsState, setColumnsState] = useState<any[]>([]);
  const [tableColumns, setTableColumns] = useState<any[]>([]);

  const [resultTableColumns, setResultTableColumns] = useState<any[]>([]);
  const [resultTableData, setResultTableData] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [queryScope, setQueryScope] = useState(false);
  const [isRun, setIsRun] = useState(false);
  const DEFAULT_RUN_RESULT = {
    query: undefined,
    page: 1,
    perPage: 10,
    total: 0,
    totalPages: 0,
  };
  const [runResult, setRunResult] = useState(DEFAULT_RUN_RESULT);

  const resetRunResult = () => {
    setRunResult(DEFAULT_RUN_RESULT);
  };

  const [savedQueryId, setSavedQueryId] = useState("");
  const [folderId, setFolderId] = useState("");
  const [timer, setSearchTimer] = useState<number | null>(null);
  const [selectedCriteria, setSelectedCriteria] = useState<SelectedCriteriaState>({
    logic: undefined,
    criteria: undefined,
  });

  const [criteriaOpen, setCriteriaOpen] = useState(false);
  // const initailSort = {
  //   sortBy: undefined,
  //   sortDir: "ASC",
  // };
  type SortItem = { field: string; direction: 'ASC' | 'DESC' };
  const [sort, setSort] = useState<SortItem[]>([]);
  const [addNewQuery, { isLoading: addQueryLoading }] =
    useAddNewQueryMutation();
  const [saveNewQuery, { isLoading: saveQueryLoading }] =
    useSaveQueryMutation();
  const [getQueryData, { isLoading: getQueryLoading }] =
    useGetQueryByIdMutation();

  const [runQuery, { isLoading: runQueryLoading }] = useRunQueryMutation();
  // const { data: columns, refetch, isLoading: columnsLoading } = useGetColumnsByScopeQuery(
  //   { scope: item?.scope },
  //   { refetchOnMountOrArgChange: true }
  // );

  const { data: columns, refetch, isLoading: columnsLoading } = useGetColumnsByScopeAllPagesQuery(
    { scope: item?.scope },
    { refetchOnMountOrArgChange: true }
  );

  // function getTopParentName(
  //   folderId: number = Number(querydata?.folderId) || 0,
  //   data: any[] = querydata?.folders || [],
  //   allowedTopParents = ["PUBLIC", "PRIVATE"]
  // ) {
  //   for (const parent of data) {
  //     if (!allowedTopParents.includes(parent.name)) continue;

  //     // Check if it's the top-level folder
  //     if (parent.id === folderId) return parent.name;

  //     // Check children
  //     for (const child of parent.children || []) {
  //       if (child.id === folderId) return parent.name;

  //       // Check grandChildren
  //       for (const grandChild of child.grandChildren || []) {
  //         if (grandChild.id === folderId) return parent.name;
  //       }
  //     }
  //   }

  //   return null; // Not found under ORGANIZATION or PRIVATE
  // }

  // useEffect(() => {
  //   if (disableQuery) {
  //     setTableData([]);
  //     setTableColumns([]);
  //     setSelectedColumns([]);
  //   }
  // }, [disableQuery])

  const callQuery = () => {
    const call = addNewQuery;
    const logicValue = normalizeLogicChoice(selectedCriteria?.logic);
    const criteriaConditions = convertCriteriaItemsToPayload(
      selectedCriteria?.criteria,
      true
    );

    const val = {
      name: item?.name,
      parentFolderId: item?.folderId,
      scope: item?.scope,
      description: item?.description,
      queryPayload: {
        selectedColumns: selectedColumns?.map((column) => column?.field),
        sorting: sort.length > 0
          ? sort
          : selectedColumns.length > 0
            ? [{ field: selectedColumns[0].field, direction: 'ASC' }]
            : [],
        ...(criteriaConditions.length
          ? {
            criteria: {
              logic: logicToOperator(logicValue),
              conditions: criteriaConditions,
            },
          }
          : {}),
      },
    };

    call(val)
      .unwrap()
      .then((res: any) => {
        if (res?.results.length > 0) {
          const sampleRow = res?.results[0];
          const columns = Object?.keys(sampleRow)
            .map((key) => ({
              id: key,
              label: formatLabel(key),
              isSortable: true,
            }));

          setTableColumns(columns);
          const transformedData = res?.results.map((item: any) => {
            const transformedItem: Record<string, any> = {};

            columns.forEach((col) => {
              transformedItem[col.id] = item[col.id] ?? null;
            });

            return transformedItem;
          });

          setTableData(transformedData);
          setTableConfig({
            ...tableConfig,
            columnOrder: columns?.map((col) => col?.id),
            visibleColumns: columns?.map((col) => col.id),
          });
        } else {
          setTableData([]);
          setTableColumns([]);
        }
      }).catch((error: any) => {
        // 👇 handle API errors like 400, 500
        showToast("Error", error?.data?.message || "Failed to add query", "error");
      });;
  };

  const saveQuery = async () => {
    if (selectedColumns?.length === 0) {
      showToast(
        "Action Failed",
        "Select at least 1 column to save query",
        "error"
      );
      return false;
    }

    const call = saveNewQuery;

    const logicValue = normalizeLogicChoice(selectedCriteria?.logic);
    const criteriaConditions = convertCriteriaItemsToPayload(
      selectedCriteria?.criteria,
      false
    );

    const val = {
      name: item?.name,
      parentFolderId: item?.folderId,
      scope: item?.scope,
      description: item?.description,
      queryPayload: {
        selectedColumns: selectedColumns?.map((column) => column?.field),
        sorting: sort.length > 0
          ? sort
          : selectedColumns.length > 0
            ? [{ field: selectedColumns[0].field, direction: 'ASC' }]
            : [],
        ...(criteriaConditions.length
          ? {
            criteria: {
              logic: logicToOperator(logicValue),
              conditions: criteriaConditions,
            },
          }
          : {}),
      },
    };

    try {
      const res = await call({ id: savedQueryId, ...val }).unwrap();

      if (res?.error) {
        showToast("Action Failed", `Failed to add query`, "error");
        return false;
      }

      if (res) {
        setSavedQueryId(res?.id);
        updateInitialState();
        showToast(
          "Success",
          `Query ${res?.id ? "Updated" : "Saved"} successfully`,
          "success"
        );
        return res?.id;
      }
    } catch (err) {
      showToast("Error", "Failed to save query", "error");
      return false;
    }
  };

  const runTheQuery = async (manualRun = false) => {
    if (selectedColumns?.length === 0) {
      showToast(
        "Action Failed",
        "Select at least 1 column to run query",
        "error"
      );
      return;
    }

    const res = await saveQuery(); // wait for save to complete
    if (res) {
      runQueryResults(savedQueryId, manualRun);
    }
  };

  const isRunningRef = useRef(false);
  // When loading a saved query on mount we don't want to immediately trigger
  // the live `callQuery()` effect. Use this ref to suppress the first automatic run
  // while we hydrate `selectedColumns` from the saved query.
  const suppressAutoRunRef = useRef(true);

  const runQueryResults = (id: string, manualRun = false) => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    try {
      const call = runQuery;
      const val = {
        id: id,
        page: runResult?.page,
        perPage: runResult?.perPage,
        query: runResult?.query,
      };
      call(val)
        .unwrap()
        .then((res: any) => {
          if (res?.error) {
            showToast("Action Failed", `Failed to run query`, "error");
            setIsRun(false);
            return;
          }

          if (res?.results?.length > 0 || res?.searchTemplate) {
            const sampleRow = res?.results[0] ?? {};
            const columns = Object?.keys(sampleRow)
              ?.map((key) => ({
                id: key,
                label: formatLabel(key),
                isSortable: false,
              }));

            setResultTableColumns(columns);
            const transformedData = res?.results.map((item: any) => {
              const transformedItem: Record<string, any> = {};

              columns.forEach((col) => {
                transformedItem[col.id] = item[col.id] ?? null;
              });

              return transformedItem;
            });

            setResultTableData(transformedData);
            setTableConfig({
              ...tableConfig,
              columnOrder: columns?.map((col) => col?.id),
              visibleColumns: columns?.map((col) => col.id),
            });

            setRunResult((prev) => {
              const newPage = res?.page;
              const newPerPage = res?.perPage;
              const newTotal = res?.total;
              const newTotalPages = Math.ceil((res?.total ?? 0) / res.perPage);

              if (
                prev.page === newPage &&
                prev.perPage === newPerPage &&
                prev.total === newTotal &&
                prev.totalPages === newTotalPages
              ) {
                return prev;
              }

              return {
                ...prev,
                page: newPage,
                perPage: newPerPage,
                total: newTotal,
                totalPages: newTotalPages,
              };
            });
            if (manualRun) {
              showToast("Success", "Query run successfully", "success");
            }
            setIsRun(true);

            if (!shouldRun && id) {
              sessionStorage.setItem(`queryRunState_${id}`, JSON.stringify({
                isRun: true,
                resultTableData: transformedData,
                resultTableColumns: columns,
                runResult: {
                  page: res.page,
                  perPage: res.perPage,
                  total: res.total,
                  totalPages: Math.ceil(res.total / res.perPage),
                }
              }));
            }

          } else {
            setResultTableData([]);
            setResultTableColumns([]);
          }
        }).catch((err: any) => showToast("Error", "Failed to run query", "error"));
    } finally {
      isRunningRef.current = false;
    }
  };

  useEffect(() => {
    if (!queryId) return;

    const saved = sessionStorage.getItem(`queryRunState_${queryId}`);
    if (!saved) return;

    try {
      const { isRun, resultTableData, resultTableColumns, runResult } = JSON.parse(saved);
      if (shouldRun && isRun) {
        setIsRun(true);
        setResultTableData(resultTableData);
        setResultTableColumns(resultTableColumns);
        setRunResult(runResult);
      } else {
        // Clear stale saved run state when opening the query in edit mode.
        sessionStorage.removeItem(`queryRunState_${queryId}`);
        setIsRun(false);
        setResultTableData([]);
        setResultTableColumns([]);
        setRunResult(DEFAULT_RUN_RESULT);
      }
    } catch (e) {
      console.warn("Failed to restore query state");
    }
  }, [queryId, shouldRun]);

  useEffect(() => {
    if (!isRun && queryId) {
      sessionStorage.removeItem(`queryRunState_${queryId}`);
    }
  }, [isRun, queryId]);

  useEffect(() => {
    if (columns) {
      const initialized = columns.map((col: any) => ({
        ...col,
        isChecked: selectedColumns.some(
          (selected) => selected.field === col.field
        ),
      }));
      setColumnsState(initialized);
    }
  }, [columns, refetch]);

  // useEffects and functions for leaving confirmation

  const updateInitialState = useCallback(() => {
    initialSelectedColumnsRef.current = selectedColumns.map((col: any) => col.field);
    initialCriteriaRef.current = JSON.parse(JSON.stringify(selectedCriteria));
    initialSortRef.current = [...sort];
  }, [selectedColumns, selectedCriteria, sort]);

  useEffect(() => {
    if (!suppressAutoRunRef.current && savedQueryId) {
      // Only capture initial state once after hydration
      if (initialSelectedColumnsRef.current.length === 0) {
        initialSelectedColumnsRef.current = selectedColumns.map((col: any) => col.field);
        initialCriteriaRef.current = JSON.parse(JSON.stringify(selectedCriteria));
        initialSortRef.current = [...sort];
      }
    }
  }, [selectedColumns, selectedCriteria, sort, savedQueryId, suppressAutoRunRef.current]);

  const hasUnsavedChanges = useCallback(() => {
    if (!savedQueryId) return false; // New query, no need to warn
    
    const currentColumns = selectedColumns.map((col: any) => col.field);
    const hasColumnChanges = JSON.stringify(currentColumns) !== JSON.stringify(initialSelectedColumnsRef.current);
    
    const hasCriteriaChanges = JSON.stringify(selectedCriteria) !== JSON.stringify(initialCriteriaRef.current);
    
    const hasSortChanges = JSON.stringify(sort) !== JSON.stringify(initialSortRef.current);
    
    return hasColumnChanges || hasCriteriaChanges || hasSortChanges;
  }, [selectedColumns, selectedCriteria, sort, savedQueryId]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges() && !navigationConfirmedRef.current) {
        // Show warning on browser refresh/close
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const useNavigationBlocker = (shouldBlock: boolean) => {
    const { navigator } = useContext(NavigationContext);
    const navigate = useNavigate();

    useEffect(() => {
      if (!shouldBlock) return;

      const push = navigator.push;
      navigator.push = (...args: Parameters<typeof push>) => {
        // Store the navigation destination
        const [to, state, options] = args;
        
        // Show warning popup instead of navigating
        setShowWarningPopup(true);
        
        // Store the navigation info to use after user confirms
        const handleConfirm = () => {
          navigationConfirmedRef.current = true;
          push(to, state, options);
        };
        
        // Attach confirm handler to window or use a context
        (window as any).__pendingNavigation = handleConfirm;
        
        return;
      };

      return () => {
        navigator.push = push;
      };
    }, [navigator, shouldBlock]);
  };

  // Use the hook in your component
  useNavigationBlocker(hasUnsavedChanges() && !navigationConfirmedRef.current);

  // useEffects and functions for leaving confirmation end

  const handleColumnToggle = (index: number) => {
    const updated = [...columnsState];
    const target = updated[index];
    target.isChecked = !target.isChecked;
    setColumnsState(updated);

    let newSelected = [...selectedColumns];
    if (target.isChecked) {
      if (!newSelected.find((col) => col.field === target.field)) {
        newSelected.push(target);
      }
    } else {
      newSelected = newSelected.filter((col) => col.field !== target.field);
      // Also remove from sort, if present
      setSort(prev => prev.filter(s => s.field !== target.field));
    }
    setSelectedColumns(newSelected);
  };

  const searchColumn = (val: string) => {
    if (timer !== null) clearTimeout(timer);

    const newTimer = setTimeout(() => {
      let baseList = columns || [];

      if (val.trim()) {
        baseList = baseList.filter((col: any) =>
          col.label.toLowerCase().includes(val.toLowerCase())
        );
      }

      const merged = baseList.map((col: any) => {
        const isChecked = selectedColumns.some(
          (selected) => selected.field === col.field
        );
        return { ...col, isChecked };
      });

      setColumnsState(merged);
    }, 400);

    setSearchTimer(newTimer as unknown as number);
  };

  function useOutsideAlerter(ref: any) {
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          setColumnsOpen(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  useOutsideAlerter(columnsOpen && inputRef);

  const STORAGE_KEY = `tableConfigSearchQuery_${item?.scope ?? "companies"}`;

  const [tableConfig, setTableConfig] = useState<TableConfig>(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved
      ? JSON.parse(saved)
      : {
        page: 1,
        perPage: 10,
        sort: undefined,
        filters: {},
        query: "",
        columnOrder: tableColumns.map((col) => col.id),
        visibleColumns: tableColumns.map((col) => col.id),
      };
  });

  // Persist tableConfig whenever it changes (and scope/storage key changes)
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tableConfig));
    } catch (e) {
      // Ignore storage errors
    }
  }, [tableConfig, STORAGE_KEY]);

  // When tableColumns changes (e.g. after running a query), ensure tableConfig has reasonable defaults
  useEffect(() => {
    if (!tableColumns || tableColumns.length === 0) return;

    setTableConfig((prev) => {
      const currentOrder = Array.isArray(prev.columnOrder) ? prev.columnOrder : [];
      const currentVisible = Array.isArray(prev.visibleColumns) ? prev.visibleColumns : [];

      // If no columnOrder saved or it doesn't match available columns, set sensible defaults
      if (currentOrder.length === 0 || currentOrder.length !== tableColumns.length) {
        return {
          ...prev,
          columnOrder: tableColumns.map((c) => c.id),
          visibleColumns: tableColumns.map((c) => c.id),
        };
      }

      // Otherwise keep existing config
      return prev;
    });
  }, [tableColumns]);

  useEffect(() => {
    // During initial hydration from a saved query we suppress the automatic live call.
    if (suppressAutoRunRef.current) return;

    if (selectedColumns?.length > 0 && !shouldRun && Array.isArray(columns) && columns.length > 0) {
      callQuery();
    } else {
      selectedCriteria?.criteria?.length === 0 &&
        showToast("Action Failed", "Select atleast 1 column to run live query", "error");
    }
  }, [selectedColumns, sort, selectedCriteria, queryId, item]);

  // If there's no queryId (new query), allow live calls immediately after mount
  useEffect(() => {
    if (!queryId) suppressAutoRunRef.current = false;
  }, [queryId]);

  // Keep selectedColumns, tableColumns and tableConfig in sync.
  // Whenever selectedColumns changes we want the tableConfig.columnOrder & visibleColumns
  // to reflect that order, and we want `tableColumns` (used by DataTable) to be
  // reordered or created to match the selectedColumns sequence.
  useEffect(() => {
    try {
      if (Array.isArray(selectedColumns) && selectedColumns.length > 0) {
        const newOrder = selectedColumns.map((c: any) => c.field);

        setTableConfig((prev: any) => {
          const prevOrder = Array.isArray(prev?.columnOrder) ? prev.columnOrder : [];
          if (JSON.stringify(prevOrder) === JSON.stringify(newOrder)) return prev;
          return { ...prev, columnOrder: newOrder, visibleColumns: newOrder };
        });

        if (tableColumns && tableColumns.length > 0) {
          const reordered = newOrder
            .map((f: string) => tableColumns.find((t) => t.id === f))
            .filter(Boolean);

          const currentIds = tableColumns.map((t) => t.id);
          const newIds = reordered.map((t: any) => t.id);

          if (JSON.stringify(currentIds) !== JSON.stringify(newIds)) {
            setTableColumns(reordered as any);
          }
        } else {
          // If tableColumns is empty (no sample row yet), create minimal columns from selectedColumns
          const created = selectedColumns.map((c: any) => ({ id: c.field, label: c.label || c.field, isSortable: true }));
          setTableColumns(created as any);
        }
      } else {
        // No selectedColumns, clear the table
        setTableData([]);
        setTableColumns([]);
        setTableConfig((prev: any) => ({ ...prev, columnOrder: [], visibleColumns: [] }));
      }
    } catch (e) {
      // swallow sync errors silently
      // console.warn('Syncing table state failed', e);
    }
    // Intentionally only depend on selectedColumns and tableColumns references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns]);

  const getQueryById = async () => {
    const call = getQueryData;

    try {
      if (!queryId) {
        showToast("Error", "Query ID is missing", "error");
        return false;
      }
      const res = await call({ id: queryId }).unwrap();

      if (res?.error) {
        showToast("Action Failed", `Failed to get query`, "error");
        return false;
      }

      if (res) {
        setSavedQueryId(res?.id);
        const serverLogic = normalizeLogicChoice(res?.queryPayload?.criteria?.logic);
        const serverCriteria = transformApiCriteriaConditions(
          res?.queryPayload?.criteria?.conditions
        );

        setSelectedCriteria({
          logic: createLogicOption(serverLogic),
          criteria: serverCriteria,
        });
        setFolderId(res?.folderId);
        // Preserve server-saved order: map saved field list to column objects
        const savedOrder: string[] = res?.queryPayload?.selectedColumns || [];
        const orderedSelected = (savedOrder || [])
          .map((field: string) => (columns || []).find((c: any) => c.field === field))
          .filter(Boolean);
        setSelectedColumns(orderedSelected as any);
        setColumnsState(
          (columns ?? [])?.map((col: any) => ({
            ...col,
            isChecked: res?.queryPayload?.selectedColumns.includes(col.field),
          }))
        );

        setSort(
          res?.queryPayload?.sorting?.map((s: any) => ({
            field: s.field,
            direction: s.direction || 'ASC',
          })) || []
        );

        showToast("Success", `Get query successfully`, "success");
        // we've hydrated state from server — allow subsequent selectedColumns changes
        // to trigger live calls
        suppressAutoRunRef.current = false;
        return res?.id;
      }
    } catch (err) {
      showToast("Error", "Failed to get query", "error");
      return false;
    }
  };

  useEffect(() => {
    if (queryId && !shouldRun && Array.isArray(columns) && columns.length > 0) {
      getQueryById();
    }
  }, [queryId, columns, item]);

  useEffect(() => {
    if (shouldRun || !queryId) return;

    !shouldRun && isRun && runTheQuery();
  }, [runResult?.page, runResult?.perPage]);

  const useDebounce = (callback: () => void, delay: number, deps: any[]) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        callback();
      }, delay);

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
  };

  useDebounce(
    () => {
      if (shouldRun || !queryId) return;

      !shouldRun && isRun && runTheQuery();
    },
    1500, // 3 seconds
    [runResult?.query]
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const newColumnOrder = Array.from(tableConfig.columnOrder || []);
      const [removed] = newColumnOrder.splice(result.source.index, 1);
      newColumnOrder.splice(result.destination.index, 0, removed);

      // update tableConfig
      setTableConfig((prev: any) => ({
        ...prev,
        columnOrder: newColumnOrder,
      }));

      // reorder tableColumns (preview) to match the new order
      if (tableColumns && tableColumns.length > 0) {
        const reorderedTableCols = newColumnOrder
          .map((id: string) => tableColumns.find((t) => t.id === id))
          .filter(Boolean);

        // only set when different
        const currentIds = tableColumns.map((t) => t.id);
        const newIds = reorderedTableCols.map((t: any) => t.id);
        if (JSON.stringify(currentIds) !== JSON.stringify(newIds)) {
          setTableColumns(reorderedTableCols as any);
        }
      }

      // reorder selectedColumns (Applied Fields) to keep in sync with table order
      if (Array.isArray(selectedColumns) && selectedColumns.length > 0) {
        const findSelectedForId = (id: string) => {
          // 1) exact field match
          let found = selectedColumns.find((s: any) => s.field === id);
          if (found) return found;

          // 2) if tableColumns has an entry for this id, try matching selected by that entry's id or label
          const tc = (tableColumns || []).find((t: any) => t.id === id || t.label === id);
          if (tc) {
            found = selectedColumns.find((s: any) => s.field === tc.id || s.label === tc.label || formatLabel(s.field) === tc.label);
            if (found) return found;
          }

          // 3) match by selected label equals id
          found = selectedColumns.find((s: any) => s.label === id || formatLabel(s.field) === id);
          if (found) return found;

          // 4) fuzzy substring matches
          found = selectedColumns.find((s: any) => {
            if (typeof s.field === "string" && typeof id === "string") {
              return (
                s.field.endsWith(id) ||
                id.endsWith(s.field) ||
                s.field.includes(id) ||
                id.includes(s.field)
              );
            }
            return false;
          });

          return found;
        };

        const reorderedSelected = newColumnOrder
          .map((id: string) => findSelectedForId(id))
          .filter(Boolean);

        // append any selectedColumns that weren't matched (preserve them at the end)
        const missing = selectedColumns.filter(
          (s: any) => !reorderedSelected.find((r: any) => r.field === s.field)
        );
        const finalSelected = [...reorderedSelected, ...missing];

        const currentSelIds = selectedColumns.map((s: any) => s.field);
        const newSelIds = finalSelected.map((s: any) => s.field);
        if (JSON.stringify(currentSelIds) !== JSON.stringify(newSelIds)) {
          setSelectedColumns(finalSelected as any);
        }
      }
    },
    [tableConfig.columnOrder, tableColumns, selectedColumns]
  );

  useEffect(() => {
    if (!shouldRun || !queryId) return;
    runQueryResults(String(queryId));
  }, [shouldRun, runResult?.page, runResult?.perPage]);

  const isFirstRender = useRef(true);
  useDebounce(
    () => {
      if (isFirstRender.current) {
        isFirstRender.current = false; // Skip first debounce
        return;
      }
      if (!shouldRun || !queryId) return;
      runQueryResults(String(queryId));
    },
    1500,
    [runResult?.query]
  );

  const hasCriteria = Array.isArray(selectedCriteria?.criteria) && selectedCriteria?.criteria.length > 0;
  const resolvedLogicValue = normalizeLogicChoice(selectedCriteria?.logic);
  const criteriaLogicLabel = hasCriteria ? resolvedLogicValue : undefined;

  const formatCriteriaDisplayValue = (val: any, isDateField: boolean) => {
    if (Array.isArray(val)) {
      return val
        .map((entry) => (isDateField ? formatDateDefault(entry) : entry))
        .join(" - ");
    }
    if (isDateField && val) {
      return formatDateDefault(val);
    }
    return val ?? "";
  };

  const renderCriteriaSummary = (
    items: CriteriaStateItem[] = [],
    depth = 0
  ): React.ReactNode[] => {
    return items.map((cri, index) => {
      if (isCriteriaGroup(cri)) {
        return (
          <li key={`${cri.id}-group-${depth}-${index}`} className="mb-1">
            <div className="d-flex align-items-center gap-2 flex-wrap" style={{ marginLeft: depth ? depth * 8 : 0 }}>
              <StyledText
                fontWeight={600}
                fontSize="14px"
                textColor={theme.CorporateTheme?.colors?.primary}
              >
                Group ({cri.logic === "Any" ? "Any" : "All"})
              </StyledText>
            </div>
            <ul className="mt-1 ms-3">
              {renderCriteriaSummary(cri.conditions, depth + 1)}
            </ul>
          </li>
        );
      }

      const foundCol = columns?.find(
        (col: any) => col?.field === cri?.column
      );
      const isDateField = ["Created", "Last Modified", 'Updated', 'Resignation Date', 'Date'].some(word =>
        foundCol?.label?.includes(word)
      );
      const displayValue = formatCriteriaDisplayValue(cri?.value, isDateField);

      return (
        <li key={`${cri.column}-${cri.condition}-${index}-${depth}`}>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-1" style={{ marginLeft: depth ? depth * 8 : 0 }}>
            <StyledText
              fontWeight={500}
              fontSize="14px"
              textColor={theme.CorporateTheme?.colors?.primary}
            >
              {foundCol?.label ?? cri?.column}
            </StyledText>
            <StyledText
              fontWeight={500}
              fontSize="14px"
              textColor={theme.CorporateTheme?.colors?.textSecondary}
            >
              {cri?.condition}
            </StyledText>
            <StyledText
              fontWeight={500}
              fontSize="14px"
              textColor={theme.CorporateTheme?.colors?.textSecondary}
            >
              {displayValue}
            </StyledText>
          </div>
        </li>
      );
    });
  };

  return (
    <>
      <div className="px-3" style={{ backgroundColor: theme?.CorporateTheme?.colors?.background }}>
        <HeaderContainer theme={theme}>
          <TitleGroup className="px-2 pt-2 pb-4">
            <div className="d-flex align-items-center gap-1">
              <StyledText fontSize="20px" fontWeight={700}>
                {item?.name}{" "}
              </StyledText>
              <StyledText textColor={theme.CorporateTheme?.colors?.textSecondary} fontSize="16px" fontWeight={500}>({item?.scope})</StyledText>
              <span style={{ opacity: item?.createdBy === userID ? 1 : 0.5 }}>
                <EditIcon
                  color={theme.CorporateTheme?.colors?.primary}
                  className="cursor-pointer"
                  onClick={() => item?.createdBy === userID ? setQueryScope(true) : null}
                  size={18}
                />
              </span>
            </div>

            <StyledText
              fontSize="14px"
              fontWeight={300}
              textColor={theme.CorporateTheme?.colors?.textSecondary}
              className="mt-1"
            >
              {"Select the fields and set criteria against that fields"}
            </StyledText>
          </TitleGroup>
        </HeaderContainer>
      </div>
      <div
        style={{
          backgroundColor: theme.CorporateTheme?.colors?.filterBG,
          position: "relative",
        }}
      >
        {isRun || shouldRun ? (
          <QueryResultsListing
            tableConfig={tableConfig}
            setTableConfig={setTableConfig}
            setIsRun={setIsRun}
            shouldRun={shouldRun}
            runResult={runResult}
            setRunResult={setRunResult}
            folderId={folderId}
            tableColumns={resultTableColumns}
            tableData={resultTableData}
            runQueryLoading={runQueryLoading || getQueryLoading || addQueryLoading}
            queryId={queryId}
            runQueryResults={runQueryResults}
            scope={item?.scope}
            queryResult={item}
            resetRunResult={resetRunResult}
          />
        ) : (
          <div
            className="row no-gutters"
            style={
              {
                "--bs-gutter-x": "0",
                height: "calc(100vh - 190px)",
              } as React.CSSProperties
            }
          >
            <Col lg={3}>
              <SimpleBar
                autoHide={false}
                className="d-flex flex-column ps-3 mt-3"
                style={{ height: "calc(100vh - 240px)" }}
              >
                {getQueryLoading ? (
                  <div className="d-flex flex-column gap-1 px-2 mt-3">
                    {Array(4)
                      .fill(null)
                      .map((_, i) => (
                        <SkeletonCell
                          key={i}
                          style={{
                            maxWidth: "100%",
                            height: "120px",
                            marginBottom: "10px",
                          }}
                        />
                      ))}
                  </div>
                ) : (
                  <>
                    <div className=" p-3 rounded" style={{ backgroundColor: theme.CorporateTheme.colors.background }}>
                      <div className="d-flex align-items-center justify-content-between">
                        <StyledText
                          className=""
                          fontWeight={700}
                          fontSize="16px"
                          textColor={theme.CorporateTheme?.colors?.headerColor}
                        >
                          Criteria
                        </StyledText>
                        <Button
                          theme={theme}
                          onClick={() => {
                            try {
                              setSearchValue("");
                              const baseList = (columns || []).map((col: any) => ({
                                ...col,
                                isChecked: selectedColumns.some(
                                  (selected) => selected.field === col.field
                                ),
                              }));
                              setColumnsState(baseList);
                            } catch { }
                            setCriteriaOpen(true);
                          }}
                          type="button"
                          btnBg="transparent"
                          $variant="customButton"
                          borderColor={theme.CorporateTheme.colors.primary}
                          color={theme.CorporateTheme.colors.primary}
                          style={{
                            height: "28px",
                          }}
                        >
                          <EditIcon color={theme.CorporateTheme.colors.primary} size={16} /> Edit Criteria
                        </Button>
                      </div>
                      {hasCriteria ? (
                        <>
                          <StyledHorizontalLine theme={theme} className="my-2" />
                          <div className="d-flex gap-2 justify-content-between">
                            <StyledText
                              fontSize="14px"
                              fontWeight={600}
                              textColor={theme.CorporateTheme?.colors?.headerColor}
                            >
                              Applied Criteria
                            </StyledText>
                            <StyledText
                              fontSize="14px"
                              fontWeight={600}
                              textColor={theme.CorporateTheme?.colors?.primary}
                            >
                              {criteriaLogicLabel}
                            </StyledText>
                          </div>
                          <StyledHorizontalLine theme={theme} className="my-2" />
                        </>
                      ) : null}
                      <ul className="mb-0 ps-3 ms-1 mt-1">
                        {hasCriteria ? (
                          renderCriteriaSummary(selectedCriteria?.criteria ?? [])
                        ) : (
                          <li>
                            <StyledText
                              fontWeight={500}
                              fontSize="14px"
                              textColor={theme.CorporateTheme?.colors?.textSecondary}
                            >
                              This query has no criteria - all records will be
                              returned
                            </StyledText>
                          </li>
                        )}
                      </ul>
                    </div>

                    <div
                      className="mt-3 py-4 px-3 rounded "
                      style={{ backgroundColor: theme.CorporateTheme.colors.background }}
                      ref={inputRef}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <StyledText
                          className=""
                          fontWeight={700}
                          fontSize="16px"
                          textColor={theme.CorporateTheme?.colors?.headerColor}
                        >
                          All Available Fields
                        </StyledText>
                      </div>

                      <div className="mt-3">
                        <AMSSelect
                          placeholder="Search and add custom fields"
                          menuPlacement="auto"
                          isMulti
                          includeAccessKey
                          showDataType
                          staticOptions={columnsState?.map((col: any) => ({
                            label: col?.label,
                            value: col?.field,
                            field: col?.field,
                            dataType: col?.dataType,
                            accessKey: col?.field,
                          }))}
                          value={selectedColumns?.map((col: any) => ({
                            label: col?.label,
                            value: col?.field,
                          }))}
                          onChange={(selected: any) => {
                            const selectedArray = Array.isArray(selected) ? selected : [];
                            const newSelectedColumns = selectedArray.map((opt: any) => {
                              const found = columns?.find((c: any) => c.field === opt.value);
                              return found || { field: opt.value, label: opt.label };
                            });
                            setSelectedColumns(newSelectedColumns);

                            setColumnsState((prev) =>
                              prev.map((item) => ({
                                ...item,
                                isChecked: selectedArray.some((opt: any) => opt.value === item.field),
                              }))
                            );

                            const newOrder = newSelectedColumns.map((c: any) => c.field);
                            setTableConfig((prev: any) => ({
                              ...prev,
                              columnOrder: newOrder,
                              visibleColumns: newOrder
                            }));

                            if (tableColumns && tableColumns.length > 0) {
                              const reordered = newOrder.map((f: string) =>
                                tableColumns.find((t) => t.id === f)
                              ).filter(Boolean);
                              setTableColumns(reordered as any);
                            }
                          }}
                        />
                      </div>
                    </div>
                    {addQueryLoading ? (
                      <div className=" mt-3  p-3 rounded " style={{ backgroundColor: theme.CorporateTheme.colors.background }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <StyledText
                            className=""
                            fontWeight={700}
                            fontSize="16px"
                            textColor={theme.CorporateTheme?.colors?.headerColor}
                          >
                            Applied Fields
                          </StyledText>
                        </div>
                        <StyledText
                          className="mt-3"
                          fontWeight={600}
                          fontSize="14px"
                          textColor={theme.CorporateTheme?.colors?.headerColor}
                        >
                          Selected Columns
                        </StyledText>

                        <div className="mt-3 d-flex flex-column gap-2">
                          {Array(selectedColumns?.length || 4)
                            .fill(null)
                            .map((_, i) => (
                              <SkeletonCell
                                key={i}
                                style={{
                                  maxWidth: "100%",
                                  height: "40px",
                                }}
                              />
                            ))}
                        </div>
                      </div>
                    ) : selectedColumns?.length > 0 ? (
                      <div className=" mt-3  p-3 rounded " style={{ backgroundColor: theme.CorporateTheme.colors.background }}>
                        <div className="d-flex align-items-center justify-content-between">
                          <StyledText
                            className=""
                            fontWeight={700}
                            fontSize="16px"
                            textColor={theme.CorporateTheme?.colors?.headerColor}
                          >
                            Applied Fields
                          </StyledText>
                        </div>
                        <StyledText
                          className="mt-3"
                          fontWeight={600}
                          fontSize="14px"
                          textColor={theme.CorporateTheme?.colors?.headerColor}
                        >
                          Selected Columns
                        </StyledText>

                        <div className="mt-3 d-flex flex-column gap-2">
                          {selectedColumns?.map((col: any, ind: number) => (
                            <SelectedBar key={col.field} theme={theme} className="d-flex align-items-center justify-content-between  me-2">
                              <StyledText
                                fontWeight={600}
                                fontSize="12px"
                                textColor={theme.CorporateTheme?.colors?.lightSecondary}
                              >
                                {col?.label}
                              </StyledText>

                              <div className="d-flex align-items-center gap-2">
                                <button
                                  type="button"
                                  aria-label={`Move ${col?.label} up`}
                                  className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                                  onClick={() => {
                                    if (ind === 0) return;
                                    const newSelected = Array.from(selectedColumns || []);
                                    const [item] = newSelected.splice(ind, 1);
                                    newSelected.splice(ind - 1, 0, item);
                                    setSelectedColumns(newSelected);
                                    const newOrder = newSelected.map((c: any) => c.field);
                                    setTableConfig((prev: any) => ({ ...prev, columnOrder: newOrder, visibleColumns: newOrder }));
                                    if (tableColumns && tableColumns.length > 0) {
                                      const reordered = newOrder.map((f: string) => tableColumns.find((t) => t.id === f)).filter(Boolean);
                                      setTableColumns(reordered as any);
                                    }
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: 2,
                                    cursor: ind === 0 ? 'not-allowed' : 'pointer',
                                    opacity: ind === 0 ? 0.4 : 1,
                                  }}
                                >
                                  <ChevronDownIcon style={{ transform: 'rotate(180deg)', width: 14, height: 14 }} color={theme.CorporateTheme.colors.textSecondary} />
                                </button>

                                <button
                                  type="button"
                                  aria-label={`Move ${col?.label} down`}
                                  className="btn btn-sm p-0 d-flex align-items-center justify-content-center"
                                  onClick={() => {
                                    if (ind === (selectedColumns?.length || 0) - 1) return;
                                    const newSelected = Array.from(selectedColumns || []);
                                    const [item] = newSelected.splice(ind, 1);
                                    newSelected.splice(ind + 1, 0, item);
                                    setSelectedColumns(newSelected);
                                    const newOrder = newSelected.map((c: any) => c.field);
                                    setTableConfig((prev: any) => ({ ...prev, columnOrder: newOrder, visibleColumns: newOrder }));
                                    if (tableColumns && tableColumns.length > 0) {
                                      const reordered = newOrder.map((f: string) => tableColumns.find((t) => t.id === f)).filter(Boolean);
                                      setTableColumns(reordered as any);
                                    }
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: 2,
                                    cursor: ind === (selectedColumns?.length || 0) - 1 ? 'not-allowed' : 'pointer',
                                    opacity: ind === (selectedColumns?.length || 0) - 1 ? 0.4 : 1,
                                  }}
                                >
                                  <ChevronDownIcon style={{ width: 14, height: 14 }} color={theme.CorporateTheme.colors.textSecondary} />
                                </button>

                                <DeleteIcon
                                  color={theme.CorporateTheme.colors.gunmetal}
                                  className="cursor-pointer"
                                  onClick={() => {
                                    setSort([]);
                                    setSelectedColumns((prev) =>
                                      prev.filter(
                                        (item) => item.field !== col.field
                                      )
                                    );

                                    setColumnsState((prev) =>
                                      prev.map((item) =>
                                        item.field === col.field
                                          ? { ...item, isChecked: false }
                                          : item
                                      )
                                    );
                                  }}
                                />
                              </div>
                            </SelectedBar>
                          ))}
                        </div>
                      </div>
                    ) : (
                      ""
                    )}

                    <div className=" mt-3  p-3 rounded" style={{ backgroundColor: theme.CorporateTheme.colors.background }}>
                      <div className="d-flex align-items-center justify-content-between">
                        <StyledText
                          className=""
                          fontWeight={700}
                          fontSize="16px"
                          textColor={theme.CorporateTheme?.colors?.headerColor}
                        >
                          Sort by:
                        </StyledText>
                      </div>
                      <div className="mt-2" >
                        <AMSSelect
                          key={`sort-options-${selectedColumns.length}-${selectedColumns.map(c => c.field).join('-')}`}
                          placeholder="Select columns to sort by"
                          staticOptions={selectedColumns.map((col) => ({
                            label: col.label,
                            value: col.field,
                          }))}
                          isMulti
                          menuPosition="fixed"
                          menuPlacement="top"
                          value={selectedColumns
                            .map((col) => ({ label: col.label, value: col.field }))
                            .filter((opt) => sort.some((s) => String(s.field) === String(opt.value)))}
                          onChange={(opts: any) => {
                            const selected = Array.isArray(opts) ? opts : [];
                            const newSortItems: SortItem[] = selected.map((opt: any) => {
                              const field = String(opt?.value ?? '');
                              const existing = sort.find((s) => String(s.field) === field);
                              return {
                                field,
                                direction: existing?.direction || 'ASC',
                              };
                            });
                            setSort(newSortItems);
                          }}
                        />
                      </div>

                      {sort.length > 0 && (
                        <div className="mt-3">
                          <div className="mt-2 d-flex flex-column gap-2">
                            {sort.map((item, idx) => (
                              <div key={item.field} className="mb-2">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                  <StyledText
                                    fontWeight={600}
                                    fontSize="14px"
                                    textColor={theme.CorporateTheme?.colors?.lightSecondary}
                                  >
                                    {selectedColumns.find(c => c.field === item.field)?.label || item.field}
                                  </StyledText>
                                  <DeleteIcon
                                    size={14}
                                    color={theme.CorporateTheme.colors.gunmetal}
                                    className="cursor-pointer"
                                    onClick={() => {
                                      setSort(prev => prev.filter(s => s.field !== item.field));
                                    }}
                                  />
                                </div>
                                <div className="d-flex align-items-center justify-content-between">
                                  {(['ASC', 'DESC'] as const).map(dir => (
                                    <StyledRadio
                                      key={dir}
                                      name={`sort-dir-${item.field}`}
                                      checked={item.direction === dir}
                                      onChange={() => {
                                        const updated = [...sort];
                                        updated[idx] = { ...updated[idx], direction: dir };
                                        setSort(updated);
                                      }}
                                      label={dir === 'ASC' ? 'Ascending Order' : 'Descending Order'}
                                      labelColor={theme.CorporateTheme?.colors?.textSecondary}
                                      radioColor={theme.CorporateTheme?.colors?.headerColor}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* <div className="d-flex align-items-center justify-content-between mt-2">
                        {["ASC", "DESC"].map((sortD) => (
                          <label
                            key={sortD}
                            className="d-flex align-items-center gap-2"
                          >
                            <input
                              type="radio"
                              name="visibility"
                              checked={sort?.sortDir === sortD}
                              style={{
                                accentColor: "#1E1E1E",
                                width: "18px",
                                height: "18px",
                              }}
                              onChange={() =>
                                setSort({ ...sort, sortDir: sortD as any })
                              }
                            />
                            <StyledText
                              fontSize="14px"
                              fontWeight={500}
                              textColor={theme.CorporateTheme?.colors?.textSecondary}
                              className="cursor-pointer"
                            >
                              {sortD === "ASC"
                                ? "Ascending order"
                                : "Descending order"}
                            </StyledText>
                          </label>
                        ))}
                      </div> */}
                    </div>
                  </>
                )}
              </SimpleBar>
            </Col>
            <Col
              lg={9}
              className="d-flex flex-column gap-2 ps-2 p-3"
              style={{ height: "calc(100vh - 240px)" }}
            >
              <div className="rounded py-2 px-3" style={{ backgroundColor: theme.CorporateTheme.colors.background }}>
                <div className="d-flex align-items-center justify-content-between">
                  <StyledText
                    className="ms-2"
                    fontSize="16px"
                    fontWeight={700}
                    textColor={theme.CorporateTheme?.colors?.headerColor}
                  >
                    Edit Query Preview
                  </StyledText>

                  <div className="d-flex align-items-center gap-2 p-2">
                    <Button
                      theme={theme}
                      className={`${(saveQueryLoading || runQueryLoading) && "class-disable"
                        }`}
                      type="button"
                      $variant="customize"
                      // disabled={tableData?.length === 0}
                      onClick={() => {
                        saveQuery();
                      }}
                    >
                      {saveQueryLoading ? (
                        <>
                          <label>
                            {savedQueryId ? "Updating" : "Saving"}...
                          </label>
                          <LoadingCircleIcon />
                        </>
                      ) : (
                        <>
                          <SaveIcon color="white" /> {savedQueryId ? "Update" : "Save"}
                        </>
                      )}
                    </Button>
                    <Button
                      theme={theme}
                      className={`${(runQueryLoading || saveQueryLoading) && "class-disable"
                        }`}
                      type="submit"
                      $variant="add"
                      disabled={tableData?.length === 0}
                      onClick={() => runTheQuery(true)}
                    >
                      {runQueryLoading ? (
                        <>
                          <label>Running...</label>
                          <LoadingCircleIcon />
                        </>
                      ) : (
                        <>
                          <FlashIcon color={theme.CorporateTheme.colors.background} /> Run
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <div className="mb-3 mt-3">
                  <DataTable
                    skeletonHeight="28px"
                    noAction
                    istableCellSpacing
                    columns={tableColumns}
                    data={tableData || []}
                    columnOrder={tableConfig.columnOrder}
                    visibleColumns={tableConfig.visibleColumns}
                    sort={tableConfig.sort}
                    noDataTitle="No Result Found"
                    noDataSubtitle="Your query result list is currently empty. Start selecting fields to keep everything organized and easy to find."
                    onDragEnd={handleDragEnd}
                    loading={addQueryLoading || runQueryLoading || getQueryLoading || columnsLoading}
                    perPage={tableConfig.perPage}
                    onAction={() => { }}
                    minWidth="1000px"
                    selectedIds={selectedIds}
                    onSelectAll={(ids: number[]) => { setSelectedIds(ids); }}
                    onSelectRow={(id: number) => {
                      setSelectedIds(prev => {
                        const newIds = prev.includes(id)
                          ? prev.filter(i => i !== id)
                          : [...prev, id];
                        return newIds;
                      });
                    }}
                    dynamicColumnsListing
                    hideActionColumn={true}
                    containerHeight={331}
                  />
                </div>
              </div>
            </Col>

            {criteriaOpen && (
              <CriteriaModal
                columns={columnsState}
                onClose={() => setCriteriaOpen(false)}
                setSelectedCriteria={setSelectedCriteria}
                selectedCriteria={selectedCriteria}
              />
            )}
          </div>
        )}
        {queryScope && (
          <QueryScopeModal
            edit
            data={item}
            id={queryId}
            onClose={() => {
              setQueryScope(false);
              refetch();
            }}
            setSelectedColumns={setSelectedColumns}
          // setColumnsState={setColumnsState}
          // refetch={refetch}
          />
        )}
        {showWarningPopup && (
          <ConfirmationPrompt 
            title="Cancel Editing?"
            subTitle="You might have unsaved changes. Are you sure you want to leave without saving?"
            onConfirm={() => {
              setShowWarningPopup(false);
              navigationConfirmedRef.current = true;
      
              // Execute pending navigation
              if ((window as any).__pendingNavigation) {
                (window as any).__pendingNavigation();
                (window as any).__pendingNavigation = null;
              }
              // navigate(-1);
            }}
            onCancel={() => setShowWarningPopup(false)}
          />
        )}
      </div>
    </>
  );
};

export default QueryCreation;

export const ActionWrapper = styled('div')<{ disabled?: boolean, removeBoxShadow?: boolean, height?: string, gap?: string, removeBorder?: boolean, borderRadius?: string }>(({ theme, disabled, removeBoxShadow, height, gap, removeBorder, borderRadius }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: gap || CorporateTheme.spacing.sm,
  height: height || '40px',
  // marginTop: CorporateTheme?.spacing.sm,
  border: removeBorder ? 'none' : `1px solid ${theme?.CorporateTheme?.colors?.border}`,
  borderRadius: borderRadius || CorporateTheme?.borderRadius,
  padding: `5px ${CorporateTheme?.spacing.sm}`,
  boxShadow: removeBoxShadow ? 'none' : '0px 1px 2px rgba(0, 0, 0, 0.1)',
  backgroundColor: theme?.CorporateTheme?.colors?.filterBG,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? '0.5' : '1',
}));

const SyledColumns = styled.div`
  display: flex;
  position: absolute;
  z-index: 999;
  top: 0;
  left: 0;
  width: 100%;
  height: 250px;
  overflow-y: auto;
  flex-direction: column;
  gap: ${CorporateTheme?.spacing?.sm};
  padding: ${CorporateTheme?.spacing?.sm};
  border: 0.5px solid ${({ theme }) => theme.CorporateTheme?.colors?.border};
  background-color: ${({ theme }) => theme.CorporateTheme?.colors?.background};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  font-size: 14px;
  box-shadow: ${CorporateTheme.shadow};
`;

const SelectedBar = styled.div`
  border-radius: 4px;
  border-width: 0.5px;
  background: ${({ theme }) => theme.CorporateTheme?.colors?.filterBG};
  border: 0.5px solid ${({ theme }) => theme.CreateFormTheme?.colors?.inputBorders};
  padding: 8px 10px;
`;
